import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Grid,
  IconButton,
  Alert,
  Autocomplete,
  Chip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import userService, { CreateUserRequest } from '../../../services/userService';
import venueService from '../../../services/venueService';
import type { Venue } from '../../../types/venue';

const UserCreate: React.FC = () => {
  const [formData, setFormData] = useState<CreateUserRequest>({
    email: '',
    firstName: '',
    lastName: '',
    role: 'ADMIN',
    password: '',
    passwordChangeRequired: true,
    assignedVenueIds: [],
  });
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [venueOptions, setVenueOptions] = useState<Venue[]>([]);
  const [venueLoading, setVenueLoading] = useState(false);
  const { isSystemAdmin } = useAuth();
  const navigate = useNavigate();

  // Redirect if not system admin
  useEffect(() => {
    if (!isSystemAdmin()) {
      navigate('/admin/dashboard');
    }
  }, [isSystemAdmin, navigate]);

  // Fetch the venue list lazily — only once VENUE_USER becomes a possibility.
  // status=all so admins can also assign users to currently-inactive venues.
  useEffect(() => {
    if (formData.role !== 'VENUE_USER' || venueOptions.length > 0 || venueLoading) return;
    setVenueLoading(true);
    venueService
      .getAllVenues(1, 200, 'name', 'asc', undefined, undefined, 'all')
      .then((res) => setVenueOptions(res.data))
      .catch(() => setError('Failed to load venues'))
      .finally(() => setVenueLoading(false));
  }, [formData.role, venueOptions.length, venueLoading]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target;
    if (name) {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  const handleVenuesChange = (_event: unknown, value: Venue[]) => {
    setFormData((prev) => ({ ...prev, assignedVenueIds: value.map((v) => v._id) }));
  };

  const selectedVenues: Venue[] =
    formData.role === 'VENUE_USER'
      ? venueOptions.filter((v) => (formData.assignedVenueIds ?? []).includes(v._id))
      : [];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Frontend guard — backend also enforces this, but failing fast keeps the UX tight.
    if (formData.role === 'VENUE_USER' && (formData.assignedVenueIds ?? []).length === 0) {
      setError('Venue users must be assigned to at least one venue.');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      // Strip assignedVenueIds for non-VENUE_USER so we don't send a stray field.
      const payload: CreateUserRequest = {
        ...formData,
        assignedVenueIds:
          formData.role === 'VENUE_USER' ? formData.assignedVenueIds ?? [] : undefined,
      };

      const newUser = await userService.createUser(payload);
      setSuccess('User created successfully');

      setFormData({
        email: '',
        firstName: '',
        lastName: '',
        role: 'ADMIN',
        password: '',
        passwordChangeRequired: true,
        assignedVenueIds: [],
      });

      setTimeout(() => {
        navigate(`/admin/users/${newUser.id ?? newUser._id}`);
      }, 1500);
    } catch (err) {
      console.error('Failed to create user:', err);
      setError('Failed to create user. Please try again later.');
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate('/admin/users', { replace: true });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper
        component="form"
        onSubmit={handleSubmit}
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={handleBack} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            Create New User
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}

        <Grid container spacing={3}>
          <Grid component="div" sx={{ gridColumn: { xs: 'span 12', md: 'span 6' } }}>
            <TextField
              fullWidth
              label="First Name"
              name="firstName"
              value={formData.firstName}
              onChange={handleInputChange}
              required
              margin="normal"
            />
          </Grid>
          <Grid component="div" sx={{ gridColumn: { xs: 'span 12', md: 'span 6' } }}>
            <TextField
              fullWidth
              label="Last Name"
              name="lastName"
              value={formData.lastName}
              onChange={handleInputChange}
              required
              margin="normal"
            />
          </Grid>
          <Grid component="div" sx={{ gridColumn: { xs: 'span 12' } }}>
            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              margin="normal"
            />
          </Grid>
          <Grid component="div" sx={{ gridColumn: { xs: 'span 12', md: 'span 6' } }}>
            <FormControl fullWidth margin="normal">
              <InputLabel id="role-label">Role</InputLabel>
              <Select
                labelId="role-label"
                name="role"
                value={formData.role}
                label="Role"
                onChange={(e) => {
                  if (e.target && 'name' in e.target && 'value' in e.target) {
                    handleInputChange(e as React.ChangeEvent<{ name?: string; value: unknown }>);
                  }
                }}
              >
                <MenuItem value="VENUE_USER">Venue User</MenuItem>
                <MenuItem value="ADMIN">Admin</MenuItem>
                <MenuItem value="SYSTEM_ADMIN">System Admin</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid component="div" sx={{ gridColumn: { xs: 'span 12', md: 'span 6' } }}>
            <TextField
              fullWidth
              label="Password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleInputChange}
              required
              margin="normal"
            />
          </Grid>
          {formData.role === 'VENUE_USER' && (
            <Grid component="div" sx={{ gridColumn: { xs: 'span 12' } }}>
              <Autocomplete
                multiple
                options={venueOptions}
                value={selectedVenues}
                onChange={handleVenuesChange}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(a, b) => a._id === b._id}
                loading={venueLoading}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip {...getTagProps({ index })} key={option._id} label={option.name} />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Assigned Venues"
                    placeholder={selectedVenues.length === 0 ? 'Select one or more venues' : ''}
                    margin="normal"
                    // Don't pass `required` — MUI Autocomplete's underlying
                    // <input> stays empty even when chips are selected, so
                    // the native HTML5 check would always fail. Validation
                    // is enforced in handleSubmit instead.
                    error={(formData.assignedVenueIds ?? []).length === 0}
                    helperText="Venue users can only access the venues you assign here."
                  />
                )}
              />
            </Grid>
          )}
          <Grid component="div" sx={{ gridColumn: { xs: 'span 12' } }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.passwordChangeRequired}
                  onChange={handleSwitchChange}
                  name="passwordChangeRequired"
                  color="primary"
                />
              }
              label="Require Password Change on First Login"
              sx={{ mt: 2 }}
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={handleBack}
            sx={{ mr: 2 }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            disabled={saving}
          >
            {saving ? 'Creating...' : 'Create User'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default UserCreate;
